import React from 'react'
import {
    Box,
    Card,
    CardContent,
    CardMedia,
    Typography,
    IconButton,
    useTheme,
} from '@mui/material'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import PersonIcon from '@mui/icons-material/Person'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { alpha } from '@mui/material/styles'
import dayjs from 'dayjs'

import { TEMPLATE_EVENT_ID } from '../../constants/constant'
import { InfoBox } from '../common/StyledComponents'

const EventCard = ({ event, onClick, onDelete }) => {
    const handleDelete = (e) => {
        e.stopPropagation();
        if (event.eventId === TEMPLATE_EVENT_ID) {
            return;
        }
        if (onDelete && typeof onDelete === 'function') {
            onDelete(event);
        }
    };

    return (
        <Card
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                position: 'relative',
                borderRadius: 2,
                boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
                transition: 'transform 0.2s, box-shadow 0.2s',
                cursor: 'pointer',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                },
                '&:hover .delete-button': {
                    opacity: event.eventId === TEMPLATE_EVENT_ID ? 0 : 1,
                },
            }}
            onClick={onClick}
        >
            <Box sx={{ position: 'relative', width: '100%', pt: '56.25%' /* 16:9 aspect ratio */ }}>
                <CardMedia
                    component="img"
                    image={event.venue.coverPhoto}
                    alt={event.title}
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                    }}
                />
            </Box>
            
            <Box sx={{ p: 2 }}>
                {/* Venue Type Tag */}
                <Typography variant="caption">
                    {event.venue.title || 'Venue not available'}
                </Typography>

                <Typography variant="h6" component="h3">
                    {event.title}
                </Typography>

                {/* Info Boxes Row */}
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    {/* Date Info */}
                    <InfoBox sx={{ 
                        py: 0.5, 
                        px: 1.5, 
                        minHeight: '22px',
                        '& .MuiTypography-root': {
                            fontSize: '0.7rem',
                        },
                        '& .MuiSvgIcon-root': {
                            fontSize: '0.875rem',
                        },
                    }}>
                        <CalendarTodayIcon sx={{ mr: 0.5 }} />
                        <Typography>
                            {dayjs(event.eventStartDate).format('MM/DD/YYYY')}
                        </Typography>
                    </InfoBox>
                    
                    {/* Guest Count Info */}
                    <InfoBox sx={{ 
                        py: 0.5, 
                        px: 1.5, 
                        minHeight: '22px',
                        '& .MuiTypography-root': {
                            fontSize: '0.7rem',
                        },
                        '& .MuiSvgIcon-root': {
                            fontSize: '0.875rem',
                        },
                    }}>
                        <PersonIcon sx={{ mr: 0.5 }} />
                        <Typography>
                            {event.guestCount || 0} guests
                        </Typography>
                    </InfoBox>
                </Box>

                {/* Time Range */}
                <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                    }}
                >
                    {/* {dayjs(event.eventStartDate).format('h:mm A')} - {dayjs(event.eventEndDate).format('h:mm A')} */}
                </Typography>
            </Box>

            {/* Delete Button */}
            {onDelete && event.eventId !== TEMPLATE_EVENT_ID && (
                <Box
                    className="delete-button"
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        opacity: 0,
                        transition: 'all 0.2s',
                        backgroundColor: 'white',
                        borderRadius: '50%',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        zIndex: 1,
                    }}
                >
                    <IconButton
                        size="small"
                        onClick={handleDelete}
                        sx={{ 
                            color: 'error.main',
                            '&:hover': {
                                backgroundColor: alpha('#FF4842', 0.08),
                            }
                        }}
                    >
                        <DeleteOutlineIcon />
                    </IconButton>
                </Box>
            )}
        </Card>
    )
}

export default EventCard
